import { useContext, useCallback } from 'react';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { I18nContext, type I18nModel } from '@bingads-webui-react/i18n-context';

export type FloatingBannerErrorCode = string | number;

export interface FloatingBannerError {
  Code?: FloatingBannerErrorCode;
  Error?: FloatingBannerErrorCode;
}

export type FloatingBannerErrorMapping = Readonly<Record<string, string>>;
export type FloatingBannerMessageParams = Parameters<I18nModel['getString']>[1];

const FLOATING_BANNER_ID = 'floatingBannerId';
const DEFAULT_ERROR = _TL_('An unidentified error has occurred. Please try again later.');
const ERROR_MAPPING: FloatingBannerErrorMapping = {};

export function useFloatingBannerApi(errorMapping: FloatingBannerErrorMapping = ERROR_MAPPING) {
  const i18n = useContext(I18nContext);

  const showWarning = useCallback((error?: FloatingBannerError | null) => {
    const errorCode = error && (error.Code || error.Error);
    const errorMessage = errorMapping[errorCode as string] || DEFAULT_ERROR;
    notificationApi.showWarning(i18n.getString(errorMessage));
  }, [errorMapping, i18n]);

  const showInfo = useCallback((error?: FloatingBannerError | null) => {
    const errorCode = error && (error.Code || error.Error);
    const errorMessage = errorMapping[errorCode as string] || DEFAULT_ERROR;
    notificationApi.showInfo(i18n.getString(errorMessage));
  }, [errorMapping, i18n]);

  const showWarningMessage = useCallback((warningMsg: string, obj: FloatingBannerMessageParams = {}) => notificationApi.showWarning(i18n.getString(warningMsg, obj)), [i18n]);

  const showInfoMessage = useCallback((infoMsg: string, obj: FloatingBannerMessageParams = {}) => {
    notificationApi.showInfo(i18n.getString(infoMsg, obj));
  }, [i18n]);

  const showCustomizedWarning = useCallback((warningObj: string) => {
    notificationApi.showWarning(warningObj);
  }, []);

  const showCustomizedInfo = useCallback((infoObj: string) => {
    notificationApi.showInfo(infoObj);
  }, []);

  const dismissFloatingBanner = useCallback((id?: string) => {
    notificationApi.dismiss(id || FLOATING_BANNER_ID);
  }, []);

  return {
    showWarning,
    showInfo,
    showWarningMessage,
    showInfoMessage,
    showCustomizedWarning,
    showCustomizedInfo,
    dismissFloatingBanner,
  };
}

export type FloatingBannerApi = ReturnType<typeof useFloatingBannerApi>;
